import { Option, ReadonlyArray } from "@morphism/fp";
import * as Ant from "antd";
import React from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Asset as GenericAsset } from "~/Asset";
import * as Color from "~/Color";
import { Distance as GenericDistance } from "~/Distance";
import * as Icon from "~/Icon";
import { ID } from "~/ID";
import * as Log from "~/Log";
import { Mutable } from "~/Mutable";
import { Order } from "~/Order";
import { Table as GenericTable } from "~/TableV2";

import { Branch } from "./Branch";
import { Date } from "./Date";
import { Driver } from "./Driver";
import { Issues } from "./Issues";
import { Vehicle } from "./Vehicle";

export function Table({
  missingDVIRs,
  isLoadingSummaries,
  isLoadingDvirSummaries,
  isLoading,
  isLoadingShifts,
  hasDvirSummaries,
  summaries,
  driver,
}: Table.Props) {
  const updateURL = Log.Summaries.Table.Settings.useURLUpdate();
  React.useEffect(() => updateURL(), []);

  const rowRoute = Log.Summaries.Table.Settings.useRowRoute();
  React.useEffect(() => rowRoute(), []);

  const onChange: React.ComponentProps<typeof GenericTable>["onChange"] = (
    _pagination,
    filters
  ) => {
    return updateURL({
      ...pipe(
        Option.fromNullable(filters["dateTime"]?.[0]),
        Option.chain(Date.Filter.Value.toInterval),
        Option.getOrElseW(() => ({ interval: undefined }))
      ),
      issues: pipe(
        Option.fromNullable(filters["issues"]),
        Option.fold(
          () => [],
          ReadonlyArray.filter(
            (issue): issue is Log.Summary.Issue => typeof issue === "string"
          )
        )
      ),
    });
  };

  const tableIsLoading = missingDVIRs
    ? isLoading
    : isLoadingSummaries;

  return (
    <GenericTable
      rowKey={({ summary }) =>
        `${summary.dateTime}-${summary.driver.id}-${
          summary.shift?.assets && summary.shift?.assets.length > 0
            ? summary.shift?.assets[0].asset.id
            : ""
        }`
      }
      dataSource={summaries as Mutable<typeof summaries>}
      loading={tableIsLoading}
      onChange={onChange as never}
      columns={Table.useColumns({
        summaries,
        missingDVIRs,
        driver,
      })}
      pagination={{
        showSizeChanger: true,
        position: ["bottomCenter"],
        disabled: tableIsLoading,
        showTotal: () =>
          !hasDvirSummaries ||
          isLoading ||
          isLoadingDvirSummaries ||
          isLoadingShifts ? (
            <Ant.Progress
              percent={Math.round(
                (isLoadingSummaries ? 0 : 25) +
                  (isLoadingShifts ? 0 : 50) +
                  (isLoadingDvirSummaries ? 0 : 25)
              )}
              type="line"
              strokeLinecap="square"
              strokeColor={Color.azure()}
              style={{
                alignSelf: "center",
                paddingRight: "8px",
                width: "112px",
              }}
            />
          ) : null,
      }}
    />
  );
}

export namespace Table {
  export type Props = Log.Summaries.PropsWithData & {
    readonly isLoadingSummaries: boolean;
    readonly isLoadingDvirSummaries: boolean;
    readonly isLoadingShifts: boolean;
    readonly hasDvirSummaries: boolean;
  };

  export const useColumns = ({
    summaries,
    missingDVIRs,
    driver,
  }: Log.Summaries &
    Pick<
      Props,
      | "missingDVIRs"
      | "driver"
    >) => {
    const settings = Table.Settings.use();
    const [filteredVehicles, setFilteredVehicles] = React.useState<
      readonly ID[] | undefined
    >(undefined);

    const vehicle = Vehicle.column({
      summaries,
      filter: filteredVehicles,
      onFilterChange: setFilteredVehicles,
    });

    const branchAndLocationNames = Branch.uniqueBranchAndLocationNames({
      summaries,
    });

    const branchColumn = Branch.column({
      branchAndLocationNames,
    });

    return [
      Date.column(settings),
      ...(driver ? [] : [Driver.column()]),
      Distance.column(),
      ...(missingDVIRs ? [vehicle] : []),
      ...(missingDVIRs ? [] : [Duration.column()]),
      ...(missingDVIRs ? [] : [Exemption.column()]),
      ...(missingDVIRs ? [] : [Issues.column(settings)]),
      ...(missingDVIRs ? [] : [branchColumn]),
      Actions.column(),
    ];
  };

  function Actions({ summary }: Log.Summary) {
    const menu = (
      <Ant.Menu>
        <Ant.Menu.Item onClick={Log.Route.use(summary).go}>
          <Icon.ExternalLinkSquareAlt />
          View this day’s logs
        </Ant.Menu.Item>
        {pipe(
          // TODO: Make optional to obey hook rules
          GenericAsset.useHREF(
            summary.shift?.assets[0] ?? { asset: { id: "" } }
          ),
          (href) =>
            summary.shift?.assets[0] && (
              <Ant.Menu.Item
                onClick={() => window.open(href, "_blank")?.focus()}
              >
                <Icon.Truck />
                View this asset
              </Ant.Menu.Item>
            )
        )}
      </Ant.Menu>
    );

    return <Ant.Dropdown.Button overlay={menu} />;
  }

  namespace Actions {
    export const column = () => ({
      title: "",
      dataIndex: "actions",
      render: (_: unknown, summary: Log.Summary) => <Actions {...summary} />,
    });
  }

  export type Settings = Date.Settings & Issues.Settings;
  export namespace Settings {
    export type GetParameter = (parameterName: string) => Option<string>;

    export const isEmpty = (settings: Settings): boolean =>
      Date.Settings.isEmpty(settings) && Issues.Settings.isEmpty(settings);

    export const encode = (settings: Partial<Settings>): string =>
      `?${[
        ...Date.Settings.encode(settings),
        ...Issues.Settings.encode(settings),
      ].join("&")}`;

    export const parse = (query: string) =>
      pipe(
        new URLSearchParams(query),
        (query): GetParameter => (parameterName) =>
          pipe(query.getAll(parameterName), ReadonlyArray.last),

        (getParameter) => ({
          ...Date.Settings.parse(getParameter),
          ...Issues.Settings.parse(getParameter),
        })
      );

    export const use = (): Settings => parse(useLocation().search);
    export const useURLUpdate = () => {
      const history = useHistory();
      return (updated?: Partial<Settings>) =>
        history.push(encode(updated ?? parse(window.location.search)));
    };

    export const useRowRoute = () => {
      const history = useHistory();
      return (route?: Log.Route) => {
        // eslint-disable-next-line functional/no-expression-statement
        route && history.push(Log.Route.href(route));
      };
    };
  }

  export namespace Distance {
    export const column = () => ({
      title: "Distance",
      dataIndex: "distance",
      sorter,
      render,
    });

    const sorter = (a: Log.Summary, b: Log.Summary) =>
      GenericDistance.compare(
        { distance: Log.Summary.distanceMiles(a.summary) },
        { distance: Log.Summary.distanceMiles(b.summary) }
      );

    const render = (_: unknown, { summary }: Log.Summary) => (
      <GenericDistance distance={Log.Summary.distanceMiles(summary)} />
    );
  }

  export namespace Duration {
    export const column = () => ({
      title: "Duration",
      dataIndex: "duration",
      sorter,
      render,
    });

    const sorter = (a: Log.Summary, b: Log.Summary): Order.Ordering =>
      Order.number().compare(a.summary.duration, b.summary.duration);

    const render = (_: unknown, summary: Log.Summary) => (
      <Log.Summary.Duration {...summary} />
    );
  }

  export namespace Exemption {
    export const column = () => ({
      title: "Exemption",
      dataIndex: "exemption",
      sorter,
      render,
    });

    const sorter = (a: Log.Summary, b: Log.Summary): Order.Ordering =>
      Order.optionally(Order.string().compare)(
        a.summary.exemption?.id,
        b.summary.exemption?.id
      );

    const render = (_: unknown, { summary }: Log.Summary) => (
      <Log.Summary.Exemption summary={summary} />
    );
  }
}
